import { Outlet, Link, useLocation } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { ApiStatus } from './ApiStatus';
import { useCurrentUser, useLogout } from '../lib/api/queries';
import { hasProduct } from '../lib/auth';

export function Layout() {
  const location = useLocation();
  const { data: user } = useCurrentUser();

  // Slice 13: staff-only nav. Client users get the ClientLayout instead,
  // but even if a staff/client boundary bug puts a client user here,
  // gating every nav item on isDpasStaff means the header still doesn't
  // reveal DPAS-internal navigation surfaces.
  const isStaff = user?.isDpasStaff ?? false;

  // Nav items keyed by product. Each module's link is hidden if the tenant
  // isn't entitled — matches the server's 404 on the endpoints, so the SPA
  // never offers a product the backend would refuse to serve.
  const showAuditTool = isStaff && user ? hasProduct(user, 'audittool') : false;
  const showDpia      = isStaff && user ? hasProduct(user, 'dpia') : false;
  // Admin link only renders for platform admins. The server's SuperAdmin
  // policy is the real gate — this just hides the link from regular users.
  const showAdmin     = isStaff && (user?.isPlatformAdmin ?? false);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-accent-600 text-white shadow-sm">
        <div className="mx-auto px-6 py-3 flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            {/* Brand mark served from src/DPAS.Client/public/dpas-diamond-logo.png.
                Replace the file in public/ to swap artwork — no rebuild needed. */}
            <img
              src="/dpas-diamond-logo.png"
              alt="DPAS"
              className="w-9 h-9 object-contain"
            />
            <Link to="/" className="text-xl font-semibold tracking-tight">
              DPAS Suite
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <ApiStatus />
            <UserMenu />
          </div>
        </div>
        <nav className="mx-auto px-6 pb-2 flex gap-1 text-sm">
          {/* Slice 18c — cross-module open work. Staff-gated rather than
              entitlement-gated: the dashboard is a platform surface and
              filters its own contents by what the tenant has bought. */}
          {isStaff && (
            <NavLink to="/dashboard"
              variant="purple"
              active={location.pathname.startsWith('/dashboard')}>
              Dashboard
            </NavLink>
          )}
          {showAuditTool && (
            <NavLink to="/audits"
              variant="purple"
              active={location.pathname === '/' || location.pathname.startsWith('/audits')}>
              Audits
            </NavLink>
          )}
          {showDpia && (
            <NavLink to="/dpia"
              variant="green"
              active={location.pathname.startsWith('/dpia')}>
              DPIA
            </NavLink>
          )}
          {/* Slice 14 — staff can manage client organisations here.
              Gated on isStaff (same signal as the other staff nav items)
              since any tenant that has product entitlements needs to be
              able to onboard the clients they audit for. */}
          {isStaff && (
            <NavLink to="/organisations"
              variant="green"
              active={location.pathname.startsWith('/organisations')}>
              Organisations
            </NavLink>
          )}
          {showAdmin && (
            <NavLink to="/admin/users"
              variant="purple"
              active={location.pathname.startsWith('/admin')}>
              Admin
            </NavLink>
          )}
        </nav>
        {/* Three-color strip echoes the layered diamonds in the DPAS logo:
            green primary, blue secondary, purple tertiary. */}
        <div className="h-1 flex">
          <div className="flex-1 bg-brand-500" />
          <div className="flex-1 bg-accent-500" />
          <div className="flex-1 bg-accent-alt-500" />
        </div>
      </header>

      <main className="flex-1 w-full mx-auto px-6 py-8">
        <Outlet />
      </main>

      <footer className="border-t border-gray-200 bg-white">
        <div className="mx-auto px-6 py-3 text-xs text-gray-500 flex justify-between">
          <span>DpasSuite — multi-tenant audit + DPIA platform</span>
          <span>Cookie auth · mandatory MFA</span>
        </div>
      </footer>
    </div>
  );
}

function UserMenu() {
  const { data: user } = useCurrentUser();
  const logout = useLogout();
  if (!user) return null;

  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="text-accent-100">{user.email}</span>
      <button
        onClick={() => logout.mutate()}
        disabled={logout.isPending}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md
                   bg-accent-700/40 hover:bg-accent-700/70 transition-colors
                   disabled:opacity-50"
        title="Sign out"
      >
        <LogOut className="w-3.5 h-3.5" aria-hidden />
        Sign out
      </button>
    </div>
  );
}

/**
 * Per-module nav link. Each product owns a colour from the DPAS diamond
 * (green = brand, purple = accent-alt). The inactive text stays light-green
 * so it blends with the green header; the active chip takes on the module's
 * colour to give each product a distinct visual identity.
 */
function NavLink({
  to,
  active,
  variant,
  children,
}: {
  to: string;
  active: boolean;
  variant: 'green' | 'purple';
  children: React.ReactNode;
}) {
  const activeChip =
    variant === 'purple'
      ? 'bg-accent-alt-700 text-white'
      : 'bg-brand-700 text-white';
  const inactiveHover =
    variant === 'purple'
      ? 'hover:bg-accent-alt-700/60 hover:text-white'
      : 'hover:bg-brand-700/60 hover:text-white';

  return (
    <Link
      to={to}
      className={`px-3 py-1.5 rounded-md transition-colors ${
        active ? activeChip : `text-accent-100 ${inactiveHover}`
      }`}
    >
      {children}
    </Link>
  );
}
